import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { getAnniDisponibili } from "@/lib/api";

// 🆕 Feb 2026: Tab "Comitato Solidale" sotto Agevolazioni.
// Mostra SOLO gli alunni con scheda agevolazione ATTIVA per l'anno scolastico
// selezionato. Include il calcolo agevolazione mensa (pasti × 2,50€),
// a-carico comitato solidale (agev.iscr+agev.rette+agev.mensa - dote_scuola)
// e ricevuto da comitato solidale (movimenti Cau=7 o Cau=8).
export default function ComitatoSolidalePage() {
  const [anno, setAnno] = useState(null);
  const [annoOptions, setAnnoOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [totali, setTotali] = useState({ a_carico_totale: 0, ricevuto_totale: 0 });
  const [tariffe, setTariffe] = useState({ listino_pasto: 8, agev_pasto: 5.5, agev_per_pasto: 2.5 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [popoverCod, setPopoverCod] = useState(null);
  // 🆕 A.S. movimenti (default = anno_scol; override per pescare da altra annualità)
  const [annoMov, setAnnoMov] = useState("");

  // Anni disponibili
  useEffect(() => {
    (async () => {
      try {
        const r = await getAnniDisponibili();
        const arr = r.data?.anni || [];
        setAnnoOptions(arr);
        if (!anno && arr.length) {
          // Anno "corrente" = il massimo disponibile
          setAnno(Math.max(...arr));
        }
      } catch (e) { console.error(e); }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!anno) return;
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/agevolazioni/comitato-solidale", { params: {
        anno_scol: anno,
        ...(annoMov ? { anno_movimenti: annoMov } : {}),
      } });
      setRows(r.data?.data || []);
      setTotali(r.data?.totali || { a_carico_totale: 0, ricevuto_totale: 0 });
      setTariffe(r.data?.tariffe || tariffe);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore caricamento");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [anno, annoMov]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.cognome || "").toUpperCase().includes(q) ||
      (r.nome || "").toUpperCase().includes(q) ||
      String(r.cod).includes(q) ||
      (r.codice_fiscale || "").includes(q)
    );
  }, [rows, search]);

  const totaliFiltrati = useMemo(() => {
    // 🆕 Feb 2026: totali per ogni colonna numerica (footer completo).
    const T = { isc_listino: 0, isc_pagata: 0, isc_delta: 0,
                retta_listino: 0, retta_pagata: 0, dote: 0, addebiti_dote: 0, retta_delta: 0,
                pasti: 0, x8: 0, mensa_pagata: 0, mensa_delta: 0,
                a_carico: 0, ricevuto: 0 };
    filtered.forEach(x => {
      T.isc_listino    += Number(x.iscrizione_listino || 0);
      T.isc_pagata     += Number(x.iscrizione_agevolata || 0);
      T.isc_delta      += Number(x.iscrizione_agevolazione || 0);
      T.retta_listino  += Number(x.retta_listino_totale || 0);
      T.retta_pagata   += Number(x.retta_agevolata_totale || 0);
      T.dote           += Number(x.dote_scuola || 0);
      T.addebiti_dote  += Number(x.addebiti_dote || 0);
      T.retta_delta    += Number(x.retta_agevolazione_totale || 0);
      T.pasti          += Number(x.pasti || 0);
      T.x8             += Number(x.pasti_x_listino || 0);
      T.mensa_pagata   += Number(x.mensa_pagata || 0);
      T.mensa_delta    += Number(x.mensa_agevolazione || 0);
      T.a_carico       += Number(x.a_carico_comitato || 0);
      T.ricevuto       += Number(x.ricevuto_da_comitato || 0);
    });
    return T;
  }, [filtered]);

  const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtInt = (n) => Number(n || 0).toLocaleString("it-IT");

  return (
    <div style={{ padding: "12px 18px" }}>
      <h2 style={{ margin: "0 0 12px", color: "#6a1b9a" }}>🤝 Consuntivo Comitato Solidale</h2>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
        padding: 10, background: "#faf5ff", border: "1px solid #b39ddb", borderRadius: 4, marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#4a148c" }}>
          A.S.
          <select value={anno || ""} onChange={e => setAnno(Number(e.target.value) || 0)} data-testid="cs-anno"
            style={{ marginLeft: 6, padding: "4px 8px", fontSize: 12, border: "1px solid #b39ddb", background: anno ? "white" : "#e8f5e9" }}>
            <option value="0">TUTTI (aggregato)</option>
            {annoOptions.map(y => <option key={y} value={y}>{y}/{String(y + 1).slice(-2)}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#1a237e" }} title="Anno da cui pescare i movimenti Mod 7/8 (default = A.S. del report)">
          A.S. mov.
          <select value={annoMov} onChange={e => setAnnoMov(e.target.value)} data-testid="cs-anno-mov"
            style={{ marginLeft: 6, padding: "4px 8px", fontSize: 12, border: "1px solid #9fa8da" }}>
            <option value="">(uguale a A.S.)</option>
            {annoOptions.map(y => <option key={y} value={y}>{y}/{String(y + 1).slice(-2)}</option>)}
          </select>
        </label>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} data-testid="cs-search"
          placeholder="🔎 Cognome, Nome, Cod, CF..."
          style={{ padding: "4px 10px", border: "1px solid #b0bec5", fontSize: 12, width: 260 }} />
        <a
          href={anno ? `${(process.env.REACT_APP_BACKEND_URL || "")}/api/agevolazioni/comitato-solidale?anno_scol=${anno}&format=pdf${annoMov ? `&anno_movimenti=${annoMov}` : ""}` : "#"}
          target="_blank" rel="noopener noreferrer"
          data-testid="cs-pdf"
          style={{
            padding: "5px 14px", background: anno ? "#c62828" : "#bbb", color: "white",
            fontSize: 11, fontWeight: 800, textDecoration: "none", borderRadius: 3,
            pointerEvents: anno ? "auto" : "none",
          }}
          title="Scarica il tabulato in PDF A3 orizzontale"
        >
          📄 PDF A3
        </a>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "#6a1b9a", fontWeight: 700 }}>
          {loading ? "Caricamento..." : `${filtered.length} alunni · listino pasto € ${tariffe.listino_pasto} · agev € ${tariffe.agev_pasto} · agev/pasto € ${tariffe.agev_per_pasto}`}
        </span>
      </div>

      {err && (
        <div style={{ padding: 10, marginBottom: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", fontSize: 12 }}>{err}</div>
      )}

      {/* 🆕 Feb 2026: Legenda calcolo "A carico Comitato" */}
      <div data-testid="cs-legenda" style={{
        padding: "8px 12px", marginBottom: 10,
        background: "#fff3e0", border: "1px solid #ffb74d", borderLeft: "5px solid #e65100",
        borderRadius: 3, fontSize: 11, color: "#3e2723", lineHeight: 1.5,
      }}>
        <b style={{ color: "#bf360c" }}>📐 Formule Comitato Solidale:</b>
        <div style={{ marginTop: 4, fontFamily: "Consolas,monospace", fontSize: 11 }}>
          <b style={{ color: "#33691e" }}>Ret.Δ</b> ={' '}
          <span style={{ color: "#e65100" }}>+ Ret.list.</span>{' '}
          <span style={{ color: "#1b5e20" }}>− Retta Pagata</span>{' '}
          <span style={{ color: "#1b5e20" }}>− Addeb. Dote</span>
        </div>
        <div style={{ marginTop: 3, fontFamily: "Consolas,monospace", fontSize: 11 }}>
          <b style={{ color: "#880e4f" }}>Comitato</b> ={' '}
          <span style={{ color: "#33691e" }}>Isc.Δ</span>{' '}
          <span style={{ color: "#33691e" }}>+ Ret.Δ</span>{' '}
          <span style={{ color: "#33691e" }}>+ Mensa Δ</span>
        </div>
        <div style={{ marginTop: 3, fontSize: 10, color: "#5d4037" }}>
          Tutti gli importi &quot;pagati&quot; sono somme di movimenti con <b>DtaPag popolata</b>:{' '}
          <b>Isc.pagata</b> = Cau=1 Mod qualsiasi ·{' '}
          <b>Retta Pagata</b> = Cau=2 Mod ∈ {'{1,2,3}'} ·{' '}
          <b>Addeb. Dote</b> = Cau=2 Mod=5 ·{' '}
          <b>Mensa Pagata</b> = Cau=3 Mod qualsiasi ·{' '}
          <b>Mensa Δ</b> = (pasti × € {tariffe.listino_pasto}) − Mensa Pagata <i>solo se scheda con &quot;Applica tariffa agevolata&quot;</i>
        </div>
      </div>

      {/* Tabella compatta — pensata per stare in una schermata FullHD senza scroll orizzontale */}
      <div style={{ border: "1px solid #ddd", borderRadius: 3 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9, tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "3.2%" }} />{/* Cod */}
            <col style={{ width: "8.5%" }} />{/* Cog */}
            <col style={{ width: "8.5%" }} />{/* Nom */}
            <col style={{ width: "2.5%" }} />{/* Sett */}
            <col style={{ width: "3.2%" }} />{/* Classe */}
            <col style={{ width: "5%" }} />{/* ISEE */}
            <col style={{ width: "4.8%" }} />{/* IscL */}
            <col style={{ width: "4.8%" }} />{/* IscA */}
            <col style={{ width: "4.8%" }} />{/* IscAgev */}
            <col style={{ width: "5.2%" }} />{/* RetL */}
            <col style={{ width: "5.2%" }} />{/* RetA */}
            <col style={{ width: "4.8%" }} />{/* Dote */}
            <col style={{ width: "5%" }} />{/* Addeb. Dote */}
            <col style={{ width: "5.2%" }} />{/* RetAgev */}
            <col style={{ width: "3%" }} />{/* Pasti */}
            <col style={{ width: "4.8%" }} />{/* x8 */}
            <col style={{ width: "4.8%" }} />{/* Mensa Pagata */}
            <col style={{ width: "5%" }} />{/* Mensa Agev */}
            <col style={{ width: "5.8%" }} />{/* A carico */}
            <col style={{ width: "6.7%" }} />{/* Ricevuto */}
          </colgroup>
          <thead style={{ position: "sticky", top: 0, background: "#f3e5f5", zIndex: 1 }}>
            <tr>
              <th style={hdr()}>Cod</th>
              <th style={{ ...hdr(), textAlign: "left" }}>Cognome</th>
              <th style={{ ...hdr(), textAlign: "left" }}>Nome</th>
              <th style={hdr()}>S.</th>
              <th style={hdr()}>Cl.</th>
              <th style={hdrRight()}>ISEE</th>
              <th style={{ ...hdrRight(), background: "#e1f5fe" }}>Isc.list.</th>
              <th style={{ ...hdrRight(), background: "#1b5e20", color: "white" }} title="Movimenti Cau=1 Mod=1/2/3 incassati">Isc.<br/>pagata</th>
              <th style={{ ...hdrRight(), background: "#c5e1a5", fontWeight: 800 }}>Isc.Δ</th>
              <th style={{ ...hdrRight(), background: "#ffe0b2" }}>Ret.list.</th>
              <th style={{ ...hdrRight(), background: "#1b5e20", color: "white" }} title="Movimenti Cau=2 Mod=1/2/3/4 incassati">Retta<br/>Pagata</th>
              <th style={hdrRight()}>Dote</th>
              <th style={{ ...hdrRight(), background: "#1b5e20", color: "white" }} title="Movimenti Cau=2 Mod=5 (Dote Scuola) incassati">Addeb.<br/>Dote</th>
              <th style={{ ...hdrRight(), background: "#c5e1a5", fontWeight: 800 }} title="Retta Agev = Retta Listino − Retta Pagata − Addeb. Dote">Ret.Δ</th>
              <th style={{ ...hdrRight(), background: "#ffccbc" }}>Pasti</th>
              <th style={{ ...hdrRight(), background: "#ffccbc" }}>× 8</th>
              <th style={{ ...hdrRight(), background: "#1b5e20", color: "white" }} title="Movimenti Cau=3 Mod qualsiasi incassati (DtaPag valorizzata) dell'A.S. selezionato">Mensa<br/>Pagata</th>
              <th style={{ ...hdrRight(), background: "#c5e1a5", fontWeight: 800 }}>Mensa Δ</th>
              <th style={{ ...hdrRight(), background: "#f8bbd0", fontWeight: 800 }} title="A carico Comitato Solidale">Comitato</th>
              <th style={{ ...hdrRight(), background: "#c5cae9", fontWeight: 800 }} title="Ricevuto da Comitato Solidale">Ricevuto</th>
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && (
              <tr><td colSpan={20} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={20} style={{ padding: 20, textAlign: "center", color: "#888" }}>Nessun alunno agevolato trovato per l&apos;A.S. selezionato.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.cod} data-testid={`cs-row-${r.cod}`} style={{ borderTop: "1px solid #f0f0f0" }}>
                <td style={cell()}>{r.cod}</td>
                <td style={cellL()}>{r.cognome}</td>
                <td style={cellL()}>{r.nome}</td>
                <td style={cell()}>{r.settore}</td>
                <td style={cell()}>{r.classe}</td>
                <td style={cellR()}>{r.isee != null ? fmt(r.isee) : "—"}</td>
                <td style={cellR("#e1f5fe")}>{fmt(r.iscrizione_listino)}</td>
                <td style={cellR("#1b5e20", 800, "white")}>{fmt(r.iscrizione_agevolata)}</td>
                <td style={cellR("#dcedc8", 700, "#33691e")}>{fmt(r.iscrizione_agevolazione)}</td>
                <td style={cellR("#ffe0b2")}>{fmt(r.retta_listino_totale)}</td>
                <td style={cellR("#1b5e20", 800, "white")}>{fmt(r.retta_agevolata_totale)}</td>
                <td style={cellR()}>{r.dote_scuola ? fmt(r.dote_scuola) : "—"}</td>
                <td style={cellR("#1b5e20", 800, "white")}>{r.addebiti_dote ? fmt(r.addebiti_dote) : "—"}</td>
                <td style={cellR("#dcedc8", 700, "#33691e")}>{fmt(r.retta_agevolazione_totale)}</td>
                <td style={cellR("#ffccbc")}>{fmtInt(r.pasti)}</td>
                <td style={cellR("#ffccbc")}>{fmt(r.pasti_x_listino)}</td>
                <td style={cellR("#1b5e20", 800, "white")}>{fmt(r.mensa_pagata)}</td>
                <td style={cellR("#dcedc8", 700, "#33691e")}>{fmt(r.mensa_agevolazione)}</td>
                <td style={cellR("#fce4ec", 800, "#880e4f")}>{fmt(r.a_carico_comitato)}</td>
                <td style={{ ...cellR("#e8eaf6", 800, "#1a237e"), padding: 0 }}>
                  <button onClick={() => setPopoverCod(r.cod)}
                    data-testid={`cs-mov-${r.cod}`}
                    title="Dettaglio movimenti Mod 7/8"
                    style={{
                      width: "100%", padding: "3px 6px", fontSize: 9, cursor: "pointer",
                      background: "transparent", border: "none", color: "#1a237e", fontWeight: 800,
                      fontFamily: "Consolas,monospace", textAlign: "right",
                    }}>
                    {fmt(r.ricevuto_da_comitato)} 📋
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot style={{ position: "sticky", bottom: 0, background: "#4a148c", color: "white", fontWeight: 800 }}>
              <tr data-testid="cs-tfoot-totali">
                <td colSpan={6} style={{ padding: "6px 8px", textAlign: "right", fontSize: 11, background: "#4a148c" }}>TOTALI ({filtered.length}):</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#01579b", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.isc_listino)}</td>
                <td data-testid="cs-tot-isc-pagata" style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#1b5e20", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.isc_pagata)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#558b2f", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.isc_delta)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#e65100", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.retta_listino)}</td>
                <td data-testid="cs-tot-retta-pagata" style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#1b5e20", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.retta_pagata)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#4a148c", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.dote)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#1b5e20", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.addebiti_dote)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#558b2f", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.retta_delta)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#4a148c", fontFamily: "Consolas,monospace" }}>{fmtInt(totaliFiltrati.pasti)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#bf360c", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.x8)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#1b5e20", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.mensa_pagata)}</td>
                <td style={{ padding: "6px 6px", textAlign: "right", fontSize: 11, background: "#558b2f", fontFamily: "Consolas,monospace" }}>{fmt(totaliFiltrati.mensa_delta)}</td>
                <td data-testid="cs-tot-a-carico" style={{ padding: "8px 8px", textAlign: "right", fontSize: 12, background: "#880e4f", fontFamily: "Consolas,monospace" }}>€ {fmt(totaliFiltrati.a_carico)}</td>
                <td data-testid="cs-tot-ricevuto" style={{ padding: "8px 8px", textAlign: "right", fontSize: 12, background: "#1a237e", fontFamily: "Consolas,monospace" }}>€ {fmt(totaliFiltrati.ricevuto)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {popoverCod !== null && (
        <MovimentiPopover cod={popoverCod} anno={anno} annoMov={annoMov} onClose={() => setPopoverCod(null)}
          alunno={rows.find(r => r.cod === popoverCod)} />
      )}
    </div>
  );
}

const hdr = () => ({ padding: "4px 5px", textAlign: "center", fontSize: 9, borderBottom: "2px solid #6a1b9a", whiteSpace: "nowrap" });
const hdrRight = () => ({ ...hdr(), textAlign: "right" });
const cell = () => ({ padding: "2px 5px", textAlign: "center", fontFamily: "Consolas,monospace", fontSize: 9 });
const cellL = () => ({ padding: "2px 5px", textAlign: "left", fontWeight: 600, fontSize: 9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" });
const cellR = (bg, weight, color) => ({
  padding: "2px 5px", textAlign: "right", fontFamily: "Consolas,monospace", fontSize: 9,
  ...(bg ? { background: bg } : {}),
  ...(weight ? { fontWeight: weight } : {}),
  ...(color ? { color } : {}),
});

function MovimentiPopover({ cod, anno, annoMov, onClose, alunno }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(`/agevolazioni/comitato-solidale/${cod}/movimenti-cau78`, { params: {
      anno_scol: anno,
      ...(annoMov ? { anno_movimenti: annoMov } : {}),
    } })
      .then(r => { if (!cancelled) { setItems(r.data?.items || []); setTotal(r.data?.total || 0); } })
      .catch(e => { if (!cancelled) setErr(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cod, anno, annoMov]);

  const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="cs-mov-popover" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9998 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: 720, maxWidth: "94vw", maxHeight: "85vh", overflowY: "auto", padding: 18, border: "2px solid #1a237e", borderRadius: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div>
            <h3 style={{ margin: 0, color: "#1a237e", fontSize: 15 }}>📋 Ricevuto da Comitato Solidale · A.S. {anno}/{String(anno + 1).slice(-2)}</h3>
            {alunno && <div style={{ fontSize: 11, color: "#666", marginTop: 3 }}>
              [{alunno.cod}] <b>{alunno.cognome} {alunno.nome}</b> ({alunno.settore}{alunno.classe})
            </div>}
          </div>
          <button onClick={onClose} data-testid="cs-mov-close"
            style={{ padding: "4px 10px", background: "#eceff1", border: "1px solid #b0bec5", cursor: "pointer" }}>✕</button>
        </div>
        {loading && <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento...</div>}
        {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", fontSize: 12 }}>{err}</div>}
        {!loading && !err && items.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "#888" }}>
            Nessun movimento incassato con modalità di pagamento 7 o 8 per questo alunno nell&apos;anno selezionato.
          </div>
        )}
        {!loading && !err && items.length > 0 && (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead style={{ position: "sticky", top: 0, background: "#e8eaf6" }}>
                <tr>
                  <th style={{ padding: "5px 8px", textAlign: "left" }}>Data Pag.</th>
                  <th style={{ padding: "5px 8px", textAlign: "left" }}>Data Doc.</th>
                  <th style={{ padding: "5px 8px", textAlign: "center" }}>Mod</th>
                  <th style={{ padding: "5px 8px", textAlign: "left" }}>Modalità</th>
                  <th style={{ padding: "5px 8px", textAlign: "center" }}>Cau</th>
                  <th style={{ padding: "5px 8px", textAlign: "left" }}>Descrizione</th>
                  <th style={{ padding: "5px 8px", textAlign: "right" }}>Importo €</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={`${it.num}-${i}`} style={{ borderTop: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "3px 8px", fontFamily: "Consolas,monospace", color: "#1b5e20", fontWeight: 700 }}>{it.data_pag || "—"}</td>
                    <td style={{ padding: "3px 8px", fontFamily: "Consolas,monospace", color: "#37474f" }}>{it.data_doc || "—"}</td>
                    <td style={{ padding: "3px 8px", textAlign: "center", fontWeight: 700, color: "#1a237e" }}>{it.mod}</td>
                    <td style={{ padding: "3px 8px", color: "#1a237e", fontWeight: 600 }}>{it.mod_des}</td>
                    <td style={{ padding: "3px 8px", textAlign: "center", color: "#666" }}>{it.cau}</td>
                    <td style={{ padding: "3px 8px", color: "#455a64" }}>{it.descrizione || it.cau_des}</td>
                    <td style={{ padding: "3px 8px", textAlign: "right", fontFamily: "Consolas,monospace", fontWeight: 700, color: it.imp >= 0 ? "#1b5e20" : "#c62828" }}>€ {fmt(it.imp)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot style={{ background: "#1a237e", color: "white", fontWeight: 800 }}>
                <tr>
                  <td colSpan={6} style={{ padding: "6px 8px", textAlign: "right" }}>TOTALE ({items.length} movimenti):</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "Consolas,monospace" }}>€ {fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
